<script setup lang="ts">
import { I18nKey } from '@/utils/typings';
import { mdiLinkVariant } from '@mdi/js';
import { computed, ref, watch } from 'vue';
import DeviceStatusIslandThumb from './DeviceStatusIslandThumb.vue';
import { deviceDetailLocation } from './deviceStatusIslandNav';
import { STATE_INFO } from './deviceStatusIslandState';
import { DeviceStatusIslandDetectorInterfaceItem } from './deviceStatusIslandTypes';

const props = defineProps<{
  item: DeviceStatusIslandDetectorInterfaceItem;
  /** Whether the island is expanded — used to replay the connector bounce. */
  expanded?: boolean;
}>();

const stateInfo = computed(() => STATE_INFO[props.item.state]);

const bounceNonce = ref(0);
watch(
  () => props.expanded,
  (isExpanded) => {
    if (isExpanded) {
      bounceNonce.value += 1;
    }
  },
);

const showChain = computed(() => props.item.state !== 'initializing');

const showConnectedArrays = computed(() => props.item.content.connectedArrays.length > 0);
const connectedArrayNames = computed(() =>
  props.item.content.connectedArrays.map((array) => array.name).join(', '),
);
</script>

<template>
  <div class="island-row">
    <div class="island-row__header">
      <div class="island-row__title">
        <span class="island-row__name text-body-2 text-truncate">{{ item.headline }}</span>
        <span v-if="showConnectedArrays" class="island-row__connected-to text-caption">
          {{ $t('DeviceStatusIslandConnectedTo', { names: connectedArrayNames }) }}
        </span>
      </div>
      <span class="island-row__state text-body-2">
        <v-icon :color="stateInfo.color" :icon="stateInfo.icon" size="14" />
        {{ $t(stateInfo.labelKey as I18nKey) }}
      </span>
    </div>

    <div v-if="showChain" class="island-row__chain">
      <DeviceStatusIslandThumb
        :orientation="showConnectedArrays ? 'portrait' : 'landscape'"
        :width="showConnectedArrays ? 120 : undefined"
        :to="deviceDetailLocation(item.id)"
        :name="item.headline"
        :pictureId="item.content.pictureId"
        :serialNumber="item.content.serialNumber"
        :typeNumber="item.content.typeNumber"
      />
      <template v-if="showConnectedArrays">
        <template
          v-for="(array, index) in item.content.connectedArrays"
          :key="array.id ?? `${array.name}-${index}`"
        >
          <span class="island-row__connector">
            <v-icon
              :key="`${array.id ?? array.name}-${bounceNonce}`"
              class="island-row__connector-icon"
              :icon="mdiLinkVariant"
              size="20"
            />
          </span>
          <DeviceStatusIslandThumb
            orientation="portrait"
            :width="120"
            :to="array.id ? deviceDetailLocation(array.id) : undefined"
            :name="array.name"
            :pictureId="array.pictureId"
            :serialNumber="array.serialNumber"
            :typeNumber="array.typeNumber"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.island-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 4px;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    min-width: 0;
  }

  &__state {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
    font-weight: 600;
  }

  &__chain {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 6px;
    // overflow-x:auto alone makes overflow-y compute to auto too (spurious
    // vertical scrollbar clipping the thumbs); pin the y-axis to visible.
    overflow-x: auto;
    overflow-y: hidden;
  }

  &__connected-to {
    padding-top: 2px;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  &__connector {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 40px;
    height: 80px;
    color: rgba(255, 255, 255, 0.75);

    &::before,
    &::after {
      content: '';
      flex: 1 1 auto;
      height: 2px;
      border-radius: 1px;
      background: rgba(255, 255, 255, 0.28);
    }
  }

  &__connector-icon {
    transform-origin: center;
    animation: island-link-pop-in 0.6s 0.3s backwards ease-out;
  }
}

@media (prefers-reduced-motion: reduce) {
  .island-row__connector-icon {
    animation: none;
  }
}
</style>
